import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal, Pencil, Shield, Trash2 } from 'lucide-react';

const MENU_WIDTH = 176;
const MENU_ESTIMATED_HEIGHT = 148;

export default function ActionMenu({ onEdit, onDelete, onRoles }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, placement: 'bottom' });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const updatePosition = () => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < MENU_ESTIMATED_HEIGHT && rect.top > MENU_ESTIMATED_HEIGHT;
    const top = openUp ? rect.top - MENU_ESTIMATED_HEIGHT - 8 : rect.bottom + 8;
    const left = Math.max(8, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8));

    setPosition({ top, left, placement: openUp ? 'top' : 'bottom' });
  };

  useEffect(() => {
    if (!open) return undefined;

    updatePosition();

    const handleOutside = (event) => {
      const target = event.target;
      if (
        buttonRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    const handleReposition = () => updatePosition();

    document.addEventListener('mousedown', handleOutside);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      document.removeEventListener('mousedown', handleOutside);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open]);

  const toggle = () => {
    setOpen((current) => {
      if (!current) {
        requestAnimationFrame(updatePosition);
      }
      return !current;
    });
  };

  const menu = open ? createPortal(
    <div
      ref={menuRef}
      style={{ top: position.top, left: position.left }}
      className="fixed z-[200] w-44 overflow-hidden rounded-xl border border-border bg-white py-1 shadow-xl"
      data-placement={position.placement}
    >
      {onEdit && (
        <button
          type="button"
          onClick={() => { setOpen(false); onEdit(); }}
          className="flex w-full items-center gap-2 px-3 py-2.5 text-sm text-text transition hover:bg-slate-50"
        >
          <Pencil size={14} /> Editar
        </button>
      )}
      {onRoles && (
        <button
          type="button"
          onClick={() => { setOpen(false); onRoles(); }}
          className="flex w-full items-center gap-2 px-3 py-2.5 text-sm text-text transition hover:bg-slate-50"
        >
          <Shield size={14} /> Permissões
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          onClick={() => { setOpen(false); onDelete(); }}
          className="flex w-full items-center gap-2 px-3 py-2.5 text-sm text-danger transition hover:bg-red-50"
        >
          <Trash2 size={14} /> Excluir
        </button>
      )}
    </div>,
    document.body
  ) : null;

  return (
    <>
      <div className="relative inline-flex">
        <button
          ref={buttonRef}
          type="button"
          onClick={toggle}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-white text-muted transition hover:bg-slate-50 hover:text-text"
          aria-label="Ações"
          aria-expanded={open}
        >
          <MoreHorizontal size={16} />
        </button>
      </div>
      {menu}
    </>
  );
}

export function TableShell({ children }) {
  return <div className="overflow-x-auto">{children}</div>;
}

export function DataTable({ children }) {
  return <table className="min-w-full text-sm">{children}</table>;
}
